import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { NumericField, TextField } from '~/components/primitives/Field'
import type { DomainGoalRecord } from '~/models/fitnessDomain'
import { FITNESS_DOMAIN_LABELS } from '~/models/fitnessDomain'
import type { WeeklySchedule } from '~/models/trainingSchedule'
import { WEEKDAY_LABELS, type Weekday, weekdayOffset, weekdaysFrom } from '~/models/weekday'
import { setWeeklySchedule } from '~/services/domainPlanService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import { newId, type ScheduledSessionId } from '~/types/ids'
import styles from './PlanPage.module.css'

/**
 * The recurring week, edited (D-105 §7).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS EDITS THE PLAN. IT NEVER TOUCHES THE WEEK ALREADY RUNNING
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A saved change takes effect from the start of next week, and the screen says
 *  so before the button. The week on Today was frozen from the schedule in force
 *  when it began, so this write is invisible to it by construction rather than
 *  by anything here remembering — which is what lets D-099 Amendment 2 and a
 *  live daily surface both be true at once.
 *
 *  **Session identifiers are minted once and preserved across an edit.**
 *  Renaming Monday's session has to stay distinguishable from deleting it and
 *  creating another, or every save reads as a wholesale replacement in the
 *  goal's change history (D-105 §7).
 *
 *  **Rows are laid out in DOM order, from the user's own week start.** No CSS
 *  reordering: `order` and `grid-auto-flow` change what is seen without changing
 *  focus and reading order, so the schedule would announce in a different
 *  sequence than it appears (D-106 §9).
 */

interface DraftSession {
  readonly id: ScheduledSessionId
  readonly weekday: Weekday
  readonly name: string
  readonly minutes: string
}

interface ScheduleEditorProps {
  goal: DomainGoalRecord
  weekStartDay: Weekday
  effectiveFrom: CalendarDate | undefined
  onSaved: () => void
  onCancel: () => void
}

function toDraft(schedule: WeeklySchedule | undefined, weekStartDay: Weekday): DraftSession[] {
  return [...(schedule ?? [])]
    .sort(
      (left, right) =>
        weekdayOffset(left.weekday, weekStartDay) - weekdayOffset(right.weekday, weekStartDay),
    )
    .map((session) => ({
      id: session.id,
      weekday: session.weekday,
      name: session.name,
      minutes: session.expectedMinutes === undefined ? '' : String(session.expectedMinutes),
    }))
}

export function ScheduleEditor({
  goal,
  weekStartDay,
  effectiveFrom,
  onSaved,
  onCancel,
}: ScheduleEditorProps) {
  const db = useDatabase()
  const [draft, setDraft] = useState<DraftSession[]>(() =>
    toDraft(goal.weeklySchedule, weekStartDay),
  )
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const domain = FITNESS_DOMAIN_LABELS[goal.domain].toLowerCase()

  const update = (index: number, field: 'name' | 'minutes' | 'weekday', value: string): void => {
    setDraft((current) =>
      current.map((session, at) =>
        at === index
          ? {
              ...session,
              ...(field === 'weekday' ? { weekday: Number(value) as Weekday } : { [field]: value }),
            }
          : session,
      ),
    )
  }

  const save = async (): Promise<void> => {
    setBusy(true)
    setError(undefined)

    const schedule: WeeklySchedule = draft.map((session) => ({
      id: session.id,
      weekday: session.weekday,
      name: session.name.trim(),
      ...(session.minutes.trim() === '' ? {} : { expectedMinutes: Number(session.minutes) }),
    }))

    const saved = await setWeeklySchedule(db, goal.id, schedule, new Date())
    setBusy(false)
    if (!saved.ok) {
      setError(saved.error.message)
      return
    }
    onSaved()
  }

  return (
    <div class={styles.editor}>
      <h3 class={styles.planHeading}>Weekly schedule</h3>

      {/* Stated before the button, not after it. A control whose effect is
          invisible until next week has to say so while the user is deciding. */}
      <p class={styles.goalNote}>
        {effectiveFrom === undefined
          ? 'Changes start from the beginning of next week.'
          : `Changes start on ${unwrapDate(effectiveFrom)}, at the beginning of next week. This week keeps the sessions it already has.`}
      </p>

      {/* Named, because each goal is itself a list item on this screen and an
          unnamed nested list is unaddressable — by a test and by a screen
          reader moving between lists. */}
      <ul class={styles.sessionList} aria-label={`Sessions for ${domain}`}>
        {draft.map((session, index) => (
          <li key={session.id} class={styles.itemRow}>
            <label class={styles.weekdayLabel}>
              <span class={styles.weekdayLabelText}>Day</span>
              <select
                class={styles.weekdaySelect}
                /* Explicit, because a `<label>` wrapping a `<select>` takes its
                   accessible name from the whole label — which includes every
                   option's text. The name would be "Day Monday Tuesday …". */
                aria-label="Day"
                value={String(session.weekday)}
                onChange={(event) =>
                  update(index, 'weekday', (event.target as HTMLSelectElement).value)
                }
              >
                {weekdaysFrom(weekStartDay).map((weekday) => (
                  <option key={weekday} value={String(weekday)}>
                    {WEEKDAY_LABELS[weekday]}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Session"
              value={session.name}
              onInput={(value) => update(index, 'name', value)}
            />
            <NumericField
              label="Minutes"
              hint="Optional."
              suffix="min"
              value={session.minutes}
              onInput={(value) => update(index, 'minutes', value)}
            />
            <Button
              variant="secondary"
              label={`Remove ${session.name.trim() === '' ? 'this session' : session.name} from ${domain}`}
              onClick={() => setDraft((current) => current.filter((_, at) => at !== index))}
            >
              Remove
            </Button>
          </li>
        ))}
      </ul>

      {draft.length === 0 ? (
        <p class={styles.goalNote}>No sessions yet. Add the ones you intend to do each week.</p>
      ) : null}

      <Button
        variant="secondary"
        label={`Add a session to ${domain}`}
        onClick={() =>
          setDraft((current) => [
            ...current,
            { id: newId<'ScheduledSessionId'>(), weekday: weekStartDay, name: '', minutes: '' },
          ])
        }
      >
        Add a session
      </Button>

      {error === undefined ? null : (
        <p class={styles.error} role="alert">
          {error}
        </p>
      )}

      <div class={styles.actions}>
        <Button busy={busy} onClick={() => void save()} label={`Save the schedule for ${domain}`}>
          Save the schedule
        </Button>
        <Button variant="secondary" onClick={onCancel} label={`Cancel editing ${domain}`}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
